import React from 'react';
import { Tag, Award, Percent, Coffee, Cake, Gift as GiftIcon, Users, Edit3, Trash2, Calendar, Clock, ShoppingBag, ArrowRight } from 'lucide-react';

const DAY_LABELS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const PromotionCard = ({ item, onEdit, onDelete, onToggleStatus, t, hasEditPermission, hasDeletePermission }) => {
    const isReward = item.isReward;
    const isActive = item.isActive !== false;
    const isBundle = item.campaignType === 'bundle';

    const getIcon = () => {
        if (!isReward) {
            if (isBundle) return <ShoppingBag size={20} />;
            if (item.type === 'multiplier') return <Award size={20} />;
            if (item.type === 'fixed') return <Tag size={20} />;
            return <Percent size={20} />;
        }
        switch (item.rewardType) {
            case 'coffee': return <Coffee size={20} />;
            case 'cake': return <Cake size={20} />;
            default: return <GiftIcon size={20} />;
        }
    };

    const isBirthday = item.birthdayOnly === true;

    const getColor = () => {
        if (isBirthday) return '#ec4899';
        if (isReward) return '#a855f7';
        if (isBundle) return '#f59e0b';
        if (item.type === 'multiplier') return '#22c55e';
        if (item.type === 'fixed') return '#22c55e';
        return '#ef4444';
    };

    const getTypeBadge = () => {
        if (isReward) return { label: 'Ödül', bg: '#a855f720', color: '#a855f7' };
        if (isBundle) return { label: 'Kombin', bg: '#f59e0b20', color: '#f59e0b' };
        if (item.type === 'multiplier') return { label: 'Çarpan', bg: '#22c55e20', color: '#22c55e' };
        if (item.type === 'fixed') return { label: 'Sabit', bg: '#22c55e20', color: '#22c55e' };
        return { label: 'Yüzde', bg: '#ef444420', color: '#ef4444' };
    };

    const getPrimaryValue = () => {
        if (!isReward) {
            if (isBundle) return `%${item.bundleDiscountValue}`;
            if (item.type === 'multiplier') return `${item.value}x`;
            if (item.type === 'percentage') return `%${item.value}`;
            return `${item.value} ₺`;
        }
        return `${item.requiredPoints} ${t('portal.points')}`;
    };

    const getBundleDescription = () => {
        if (!isBundle) return null;
        const buyCount = item.buyItems?.length || 0;
        const getCount = item.getItems?.length || 0;
        return `${buyCount} ürün al → ${getCount} üründe %${item.bundleDiscountValue} indirim`;
    };

    const activeDays = item.activeDays || [];
    const hasSpecificDays = activeDays.length > 0 && activeDays.length < 7;

    const badge = getTypeBadge();
    const accentColor = getColor();

    return (
        <div className="card glass promotion-card-hover" style={{
            position: 'relative', transition: 'all 0.3s',
            opacity: isActive ? 1 : 0.55, filter: isActive ? 'none' : 'grayscale(100%)',
            overflow: 'hidden',
        }}>
            {/* Top accent line */}
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                background: accentColor, opacity: isActive ? 1 : 0.3,
            }} />

            {/* Header: Icon + Badge + Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', paddingTop: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                        background: accentColor,
                        color: 'white', padding: '10px', borderRadius: '12px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        {getIcon()}
                    </div>
                    <div style={{
                        padding: '3px 10px', borderRadius: '8px', fontSize: '0.7rem',
                        fontWeight: 700, background: badge.bg, color: badge.color,
                        letterSpacing: '0.02em',
                    }}>
                        {badge.label}
                    </div>
                    {!isActive && (
                        <div style={{
                            padding: '3px 10px', borderRadius: '8px', fontSize: '0.65rem',
                            fontWeight: 700, background: 'rgba(0,0,0,0.06)', color: 'var(--text-muted)',
                        }}>
                            Pasif
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {hasEditPermission && (
                        <>
                            <label className="switch" style={{ marginRight: '2px' }} title={isActive ? "Pasif Yap" : "Aktif Yap"}>
                                <input type="checkbox" checked={isActive} onChange={() => onToggleStatus(item)} />
                                <span className="slider"></span>
                            </label>
                            <button onClick={() => onEdit(item)} style={{
                                background: 'rgba(var(--primary-rgb), 0.05)', color: 'var(--primary)',
                                padding: '7px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                                transition: 'background 0.15s',
                            }} title={t('common.edit')}>
                                <Edit3 size={16} />
                            </button>
                        </>
                    )}
                    {hasDeletePermission && (
                        <button onClick={() => onDelete(item)} style={{
                            background: 'transparent', color: '#ef4444', border: 'none',
                            padding: '7px', cursor: 'pointer', borderRadius: '8px',
                            transition: 'background 0.15s',
                        }}>
                            <Trash2 size={16} />
                        </button>
                    )}
                </div>
            </div>

            {/* Title */}
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.5rem', lineHeight: 1.3 }}>
                {item.title}
                {isBirthday && (
                    <span style={{ marginLeft: '8px', fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '8px', background: '#fef3c7', color: '#92400e', verticalAlign: 'middle' }}>🎂 Doğum Günü</span>
                )}
            </h3>

            {/* Bundle description */}
            {isBundle && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    fontSize: '0.78rem', color: accentColor, fontWeight: 600,
                    marginBottom: '0.75rem',
                }}>
                    <ShoppingBag size={14} />
                    <span>{getBundleDescription()}</span>
                </div>
            )}

            {/* Primary value */}
            <div style={{
                fontSize: '1.75rem', fontWeight: 800, color: accentColor,
                marginBottom: '1rem', display: 'flex', alignItems: 'baseline', gap: '8px',
            }}>
                {getPrimaryValue()}
                <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-muted)' }}>
                    {isReward ? t('portal.points') : (isBundle ? 'İndirim' : (item.type === 'multiplier' ? t('discounts.multiplierValue') : 'İndirim'))}
                </span>
            </div>

            {/* Info section */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                {/* Target */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: 'var(--text-main)' }}>
                    <Users size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    <span style={{ fontWeight: 500 }}>{
                        item.target === 'all' ? t('discounts.targetAll') : (item.target === 'groups' || item.target === 'loyalty_status' || item.target === 'visit_role')
                            ? (() => {
                                const statuses = (Array.isArray(item.loyaltyStatus) ? item.loyaltyStatus : (item.loyaltyStatus ? [item.loyaltyStatus] : [])).filter(s => s && s.toLowerCase() !== 'loyal');
                                const roles = (Array.isArray(item.visitRole) ? item.visitRole : (item.visitRole ? [item.visitRole] : [])).filter(r => r && r.toLowerCase() !== 'loyal');
                                return [...statuses, ...roles].join(', ') || 'Seçim Yok';
                            })()
                            : t('discounts.targetSpecific')
                    }</span>
                </div>

                {/* Scope / Products */}
                {!isReward && (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: 'var(--text-main)', marginBottom: '6px' }}>
                            <Tag size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                            <span style={{ fontWeight: 500 }}>{
                                (item.productScope === 'specific_products' || (isBundle && (item.buyItems?.length > 0 || item.getItems?.length > 0)))
                                    ? t('discounts.scopeSpecific') : t('discounts.scopeAll')
                            }</span>
                        </div>

                        {/* Product chips */}
                        {(() => {
                            const products = item.productScope === 'specific_products' ? (item.selectedProducts || []) : [];
                            const buyItems = isBundle ? (item.buyItems || []) : [];
                            const getItems = isBundle ? (item.getItems || []) : [];

                            if (isBundle && (buyItems.length > 0 || getItems.length > 0)) {
                                return (
                                    <div style={{ paddingLeft: '22px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                        {buyItems.slice(0, 2).map((p, i) => (
                                            <span key={`b-${i}`} style={{
                                                padding: '2px 8px', background: `${accentColor}10`, color: accentColor,
                                                borderRadius: '6px', fontSize: '0.65rem', fontWeight: 600,
                                                border: `1px solid ${accentColor}25`,
                                            }}>{p}</span>
                                        ))}
                                        {buyItems.length > 2 && <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>+{buyItems.length - 2}</span>}
                                        <ArrowRight size={12} style={{ color: 'var(--text-muted)' }} />
                                        {getItems.slice(0, 2).map((p, i) => (
                                            <span key={`g-${i}`} style={{
                                                padding: '2px 8px', background: '#22c55e10', color: '#22c55e',
                                                borderRadius: '6px', fontSize: '0.65rem', fontWeight: 600,
                                                border: '1px solid #22c55e25',
                                            }}>{p}</span>
                                        ))}
                                        {getItems.length > 2 && <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>+{getItems.length - 2}</span>}
                                    </div>
                                );
                            }

                            const uniqueList = [...new Set(products)];
                            if (uniqueList.length === 0) return null;
                            return (
                                <div style={{ paddingLeft: '22px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {uniqueList.slice(0, 3).map((prod, idx) => (
                                        <span key={`${prod}-${idx}`} style={{
                                            padding: '2px 8px', background: 'var(--bg-main)', color: 'var(--text-muted)',
                                            borderRadius: '6px', fontSize: '0.65rem', fontWeight: 600,
                                            border: '1px solid var(--border)',
                                        }}>{prod}</span>
                                    ))}
                                    {uniqueList.length > 3 && (
                                        <span style={{
                                            padding: '2px 8px', background: 'var(--bg-main)', color: 'var(--text-muted)',
                                            borderRadius: '6px', fontSize: '0.65rem', fontWeight: 600,
                                            border: '1px solid var(--border)',
                                        }}>+{uniqueList.length - 3} daha</span>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* Active days */}
                {hasSpecificDays && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Calendar size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                        <div style={{ display: 'flex', gap: '3px' }}>
                            {DAY_KEYS.map((day, i) => {
                                const active = activeDays.includes(day);
                                return (
                                    <span key={day} style={{
                                        fontSize: '0.6rem', fontWeight: 700, width: '26px', height: '20px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        borderRadius: '5px',
                                        background: active ? accentColor : 'var(--bg-main)',
                                        color: active ? 'white' : 'var(--text-muted)',
                                        border: active ? 'none' : '1px solid var(--border)',
                                        opacity: active ? 1 : 0.5,
                                    }}>{DAY_LABELS[i]}</span>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Stats row */}
                <div style={{ display: 'flex', gap: '6px', marginTop: '0.25rem' }}>
                    <div style={{ flex: 1, padding: '8px', background: 'var(--bg-main)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: '2px' }}>Kullanım</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{item.usageCount || 0}<span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}> / {item.totalLimit || '∞'}</span></div>
                    </div>
                    <div style={{ flex: 1, padding: '8px', background: 'var(--bg-main)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: '2px' }}>Kişi Başı</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{item.customerLimit || '∞'}</div>
                    </div>
                    <div style={{ flex: 1.3, padding: '8px', background: 'var(--bg-main)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: '2px' }}>
                            <Clock size={10} style={{ marginRight: '3px', verticalAlign: 'middle' }} />Süre
                        </div>
                        <div style={{ fontSize: '0.8rem', fontWeight: 700 }}>
                            {item.expiryDate ? (
                                <span title={`${item.startDate ? new Date(item.startDate).toLocaleDateString('tr-TR') : '-'} → ${new Date(item.expiryDate).toLocaleDateString('tr-TR')}`}>
                                    {new Date(item.expiryDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                                </span>
                            ) : 'Süresiz'}
                            {(item.startTime && item.expiryTime && (item.startTime !== '00:00' || item.expiryTime !== '23:59')) && (
                                <div style={{ fontSize: '0.6rem', color: accentColor, marginTop: '1px' }}>
                                    {item.startTime}–{item.expiryTime}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PromotionCard;
